import { compact, isUndefined } from 'lodash';
import path from 'path-browserify';

import { SchoolCardFragment, SchoolInfoBoxFragment, Strapi_Enum_School_Status } from '../../graphql.schema';

export const flatSpread = (item: any, prefix = '') =>
  Array.from([item])
    .flat()
    .filter((i) => !!i)
    .map((e) => `${prefix}${e}`)
    .join(' ')
    .trim();

export interface Modifiers {
  has?: string[] | string;
  is?: string[] | string;
  reversed?: boolean;
}

export const modifierClasses = (modifiers: Modifiers = {}) =>
  [flatSpread(modifiers.has, 'has-'), flatSpread(modifiers.is, 'is-')].join(' ').trim();

export const getS3DefaultImageUrl = (filename: string) => {
  const parts = filename.split('/');
  const basename = parts[parts.length - 1];
  return `https://wse-default-images-wse-dev.s3-eu-west-1.amazonaws.com/${basename}`;
};

export const fullImageUrl = (url?: string) => {
  if (!url) return;
  if (url.match(/^https?:\/\/.+/)) return url;
  if (process.env.GATSBY_STRAPI_ENDPOINT) {
    const origin = new URL(process.env.GATSBY_STRAPI_ENDPOINT).origin;
    return `${origin}${url}`;
  }
  return `http://localhost:1337${url}`;
};

export type GeoPoint = {
  lat: number;
  lng: number;
};

export const getMapMarkersFromSchools = (schools: SchoolCardFragment[]) =>
  compact(
    schools.map((school) => {
      const { slug, name, documentId, address, status, mapMarker } = school;
      const lat = mapMarker?.mapLocation?.latitude || 0;
      const lng = mapMarker?.mapLocation?.longitude || 0;
      const popUpHtml = mapMarker?.mapLocation?.popUpHtml ?? '';
      if (isUndefined(documentId) || isUndefined(lat) || isUndefined(lng)) return undefined;
      return {
        documentId,
        lat,
        lng,
        slug,
        name,
        address,
        popUpHtml,
        mapMarker,
        isVirtualSchool: status === Strapi_Enum_School_Status.open_virtually,
      };
    }),
  );

export const generateSchemaForSchools = (school: SchoolCardFragment, schoolInfoBox: SchoolInfoBoxFragment, pageContext:any) => {
    const educationalOrganizationSchema = {
      "@type": "EducationalOrganization",
      "name": school.name,
      "telephone": school.phoneNumber,
      "email": school.emailAddress,
      "address": {
        "@type": "PostalAddress",
        ...(school?.address?.locality && { "addressLocality": school.address.locality }),
        ...(school?.address?.region && { "addressRegion": school.address.region }),
        ...(school?.address?.postalCode && { "postalCode": school.address.postalCode }),
        ...(school?.address?.streetAddress && { "streetAddress": school.address.streetAddress }),
        ...(school?.address?.country && { "addressCountry": school.address.country })
      },
      "geo": school.mapMarker?.mapLocation
        ? {
            "@type": "GeoCoordinates",
            "latitude": school.mapMarker.mapLocation.latitude,
            "longitude": school.mapMarker.mapLocation.longitude
          }
        : undefined,
      "sameAs": compact(schoolInfoBox?.socialLinks)?.map((social: any) => social.linkUrl)
    };
    const breadcrumbListSchema = {
      "@type": "BreadcrumbList",
      "itemListElement": [
        ...(pageContext?.breadcrumb?.crumbs
          ?.filter((crumb: any) => crumb.crumbLabel && crumb.pathname !== '/')
          ?.map((crumb: any, index: number) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.crumbLabel.replace(/-/g, ' '),
            "item": `${process.env.GATSBY_SITE_URL}${crumb.pathname}`
          })) ?? []),

      ]
    };
    return {
      "@context": "https://schema.org/",
      "@graph": [educationalOrganizationSchema, breadcrumbListSchema]
    };
  }

export const generateSchemaForCourse = (course: any, pageContext:any) => {
  if (!course) return null;
 
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.name || undefined,
    "description": course.short || undefined,
    "url": `${process.env.GATSBY_SITE_URL}/${pageContext?.breadcrumb?.location || ''}`,
    "image": course.image?.url || undefined,
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Wall Street English",
      "url": process.env.GATSBY_SITE_URL || undefined
    }
  };
  return schema;
};

export const generateSchemaForBreadcrumb = (entry:any,pageContext: any) => {
  const schema: any = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "headline": entry.title,
          "image": [entry.image?.url],
          "datePublished": entry.publishedAt,
          "dateModified": entry.updatedAt
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            ...(pageContext?.breadcrumb?.crumbs
              ?.filter((crumb: any) => crumb.crumbLabel && crumb.pathname !== '/')
              ?.map((crumb: any, index: number) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": crumb.crumbLabel.replace(/-/g, ' '),
                "item": `${process.env.GATSBY_SITE_URL}${crumb.pathname}`
              })) ?? [])
          ]
        }
      ]
  };
  return schema;
}
 
export const generateSchemaForEvent = (event: any, pageContext:any) => {
  const Schema :any = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": pageContext?.breadcrumb?.crumbs?.map((crumb: any, index: number) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.crumbLabel.replace(/-/g, ' '),
      "item": `${process.env.GATSBY_SITE_URL}${crumb.pathname}`
    }))
  };
  return Schema;
}

export const addTrailingSlash = (url: string): string => {
    // Skip if URL already ends with slash
  if (url.endsWith('/')) {
    return url;
  }

  // Skip if URL looks like a file (has .ext at the end)
  const lastSegment = url.split('/').pop() || '';
  if (lastSegment.includes('.')) {
    return url;
  }

  // Otherwise, add trailing slash
  return `${url}/`;
};